import { apiClient } from './apiClient';
import type { 
  CreatePageInstructionRequest,
  GetInstructionEngineRequest,
  PageInstructionDto,
  PageInstructionEngineResponseDto,
  PageInstructionListItemDto,
  PageInstructionWithPreferenceDto, 
  UpdatePageInstructionRequest,
  UpdateInstructionPreferenceRequest,
  UserInstructionPreferenceDto 
} from '../types/pageInstruction';

// ─────────────────────────────────────────────────────────────
// Page Instruction Service
// API client for contextual user guidance system
// ─────────────────────────────────────────────────────────────

const BASE_URL = '/page-instructions';

/**
 * Get page instruction with user preference.
 */
export async function getPageInstruction(
  pageRouteKey: string,
  programId?: string | null,
  roleId?: string | null
): Promise<PageInstructionWithPreferenceDto> {
  const params = new URLSearchParams({ pageRouteKey });
  if (programId) params.append('programId', programId);
  if (roleId) params.append('roleId', roleId);

  const response = await apiClient.get<{
    success: boolean;
    data: PageInstructionWithPreferenceDto;
    message: string;
  }>(`${BASE_URL}?${params.toString()}`);

  return response.data.data;
}

export async function getInstructionEngine(
  request: GetInstructionEngineRequest
): Promise<PageInstructionEngineResponseDto> {
  const params = new URLSearchParams({ pageRouteKey: request.pageRouteKey });
  if (request.programId) params.append('programId', request.programId);
  if (request.roleId) params.append('roleId', request.roleId);
  if (request.activeContextKeys && request.activeContextKeys.length > 0) {
    params.append('activeContextKeys', request.activeContextKeys.join(','));
  }

  try {
    const response = await apiClient.get<{
      success: boolean;
      data: PageInstructionEngineResponseDto;
      message: string;
    }>(`${BASE_URL}/engine?${params.toString()}`);

    return response.data.data;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === 404) {
      return createEmptyInstructionEngineResponse(request);
    }

    throw error;
  }
}

function createEmptyInstructionEngineResponse(
  request: GetInstructionEngineRequest
): PageInstructionEngineResponseDto {
  const localPreference = getLocalPreference(request.pageRouteKey, request.programId);

  return {
    instructions: [],
    primaryInstruction: null,
    hasInstruction: false,
    preference: {
      preferenceId: null,
      pageRouteKey: request.pageRouteKey,
      programId: request.programId ?? null,
      isCollapsed: localPreference?.isCollapsed ?? true,
      dontShowAgain: localPreference?.dontShowAgain ?? false,
      isFirstVisit: !localPreference,
      viewCount: localPreference?.viewCount ?? 0,
    },
  };
}

/**
 * Update user's instruction preference (collapsed/don't show).
 */
export async function updateInstructionPreference(
  request: UpdateInstructionPreferenceRequest
): Promise<UserInstructionPreferenceDto> {
  const response = await apiClient.put<{
    success: boolean;
    data: UserInstructionPreferenceDto;
    message: string;
  }>(`${BASE_URL}/preference`, request);

  return response.data.data;
}

/**
 * Reset user's preference for a page (show instruction again).
 */
export async function resetInstructionPreference(
  pageRouteKey: string,
  programId?: string | null
): Promise<void> {
  const params = new URLSearchParams({ pageRouteKey });
  if (programId) params.append('programId', programId);

  await apiClient.post(`${BASE_URL}/preference/reset?${params.toString()}`);
}

/**
 * Get all available page route keys with display names.
 */
export async function getPageRouteKeys(): Promise<{ key: string; displayName: string }[]> {
  const response = await apiClient.get<{
    success: boolean;
    data: { key: string; displayName: string }[];
    message: string;
  }>(`${BASE_URL}/route-keys`);

  return response.data.data;
}

export async function getAllInstructions(programId?: string | null): Promise<PageInstructionListItemDto[]> {
  const params = new URLSearchParams();
  if (programId) params.append('programId', programId);

  const response = await apiClient.get<{
    success: boolean;
    data: PageInstructionListItemDto[];
    message: string;
  }>(`${BASE_URL}/admin/list${params.toString() ? `?${params.toString()}` : ''}`);

  return response.data.data;
}

export async function getInstructionById(instructionId: string): Promise<PageInstructionDto> {
  const response = await apiClient.get<{
    success: boolean;
    data: PageInstructionDto;
    message: string;
  }>(`${BASE_URL}/admin/${instructionId}`);

  return response.data.data;
}

export async function createInstruction(request: CreatePageInstructionRequest): Promise<PageInstructionDto> {
  const response = await apiClient.post<{
    success: boolean;
    data: PageInstructionDto;
    message: string;
  }>(`${BASE_URL}/admin`, request);

  return response.data.data;
}

export async function updateInstruction(
  instructionId: string,
  request: UpdatePageInstructionRequest
): Promise<PageInstructionDto> {
  const response = await apiClient.put<{
    success: boolean;
    data: PageInstructionDto;
    message: string;
  }>(`${BASE_URL}/admin/${instructionId}`, request);

  return response.data.data;
}

export async function deleteInstruction(instructionId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/admin/${instructionId}`);
}

// ─────────────────────────────────────────────────────────────
// Local Storage Fallback (for offline/error scenarios)
// ─────────────────────────────────────────────────────────────

const PREFERENCE_STORAGE_KEY = 'epprem_page_instruction_preferences';

interface LocalPreference {
  isCollapsed: boolean;
  dontShowAgain: boolean;
  viewCount: number;
  lastViewed: string;
}

/**
 * Get local preference from localStorage.
 */
export function getLocalPreference(pageRouteKey: string, programId?: string | null): LocalPreference | null {
  try {
    const stored = localStorage.getItem(PREFERENCE_STORAGE_KEY);
    if (!stored) return null;

    const preferences = JSON.parse(stored) as Record<string, LocalPreference>;
    const key = programId ? `${pageRouteKey}:${programId}` : pageRouteKey;
    return preferences[key] || null;
  } catch {
    return null;
  }
}

/**
 * Save local preference to localStorage.
 */
export function saveLocalPreference(
  pageRouteKey: string, 
  preference: Partial<LocalPreference>,
  programId?: string | null
): void {
  try {
    const stored = localStorage.getItem(PREFERENCE_STORAGE_KEY);
    const preferences = stored ? JSON.parse(stored) as Record<string, LocalPreference> : {};
    const key = programId ? `${pageRouteKey}:${programId}` : pageRouteKey;

    preferences[key] = {
      isCollapsed: preference.isCollapsed ?? preferences[key]?.isCollapsed ?? true,
      dontShowAgain: preference.dontShowAgain ?? preferences[key]?.dontShowAgain ?? false,
      viewCount: (preferences[key]?.viewCount ?? 0) + 1,
      lastViewed: new Date().toISOString(),
    };

    localStorage.setItem(PREFERENCE_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Silently fail - localStorage might be full or disabled
  }
}

/**
 * Clear local preference for a page.
 */
export function clearLocalPreference(pageRouteKey: string, programId?: string | null): void {
  try {
    const stored = localStorage.getItem(PREFERENCE_STORAGE_KEY);
    if (!stored) return;

    const preferences = JSON.parse(stored) as Record<string, LocalPreference>;
    const key = programId ? `${pageRouteKey}:${programId}` : pageRouteKey;
    delete preferences[key];

    localStorage.setItem(PREFERENCE_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Silently fail
  }
}
